import {
  Children,
  type HTMLAttributes,
  type ReactNode,
  useEffect,
  useRef,
} from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Check } from "lucide-react";
import "./Stepper.css";

interface StepperProps extends Omit<HTMLAttributes<HTMLDivElement>, "onChange"> {
  children: ReactNode;
  currentStep: number;
  onBack: () => void;
  onNext: () => void | Promise<void>;
  onStepChange?: (step: number) => void;
  canContinue?: boolean;
  loading?: boolean;
  backButtonText?: string;
  nextButtonText?: string;
  completeButtonText?: string;
  disableStepIndicators?: boolean;
}

export default function Stepper({
  children,
  currentStep,
  onBack,
  onNext,
  onStepChange,
  canContinue = true,
  loading = false,
  backButtonText = "Back",
  nextButtonText = "Continue",
  completeButtonText = "Complete",
  disableStepIndicators = false,
  className = "",
  ...rest
}: StepperProps) {
  const steps = Children.toArray(children);
  const totalSteps = steps.length;
  const safeStep = Math.min(Math.max(currentStep, 1), totalSteps);
  const previousStepRef = useRef(safeStep);
  const prefersReducedMotion = useReducedMotion();
  const direction = safeStep >= previousStepRef.current ? 1 : -1;
  const isLastStep = safeStep === totalSteps;

  useEffect(() => {
    previousStepRef.current = safeStep;
  }, [safeStep]);

  const transition = prefersReducedMotion
    ? { duration: 0 }
    : { duration: 0.32, ease: [0.22, 1, 0.36, 1] as const };

  const handleIndicatorClick = (stepNumber: number) => {
    if (disableStepIndicators || loading || stepNumber === safeStep) return;
    onStepChange?.(stepNumber);
  };

  return (
    <div className={`onboarding-stepper ${className}`} {...rest}>
      <div className="onboarding-stepper__panel">
        <div className="onboarding-stepper__progress" aria-label={`Step ${safeStep} of ${totalSteps}`}>
          {steps.map((_, index) => {
            const stepNumber = index + 1;
            const status = safeStep === stepNumber ? "active" : safeStep > stepNumber ? "complete" : "upcoming";

            return (
              <div className="onboarding-stepper__progress-item" key={stepNumber}>
                <motion.button
                  type="button"
                  className={`onboarding-stepper__indicator is-${status}`}
                  aria-label={`Step ${stepNumber}${status === "complete" ? ", completed" : status === "active" ? ", current" : ""}`}
                  aria-current={status === "active" ? "step" : undefined}
                  disabled={disableStepIndicators || loading || stepNumber === safeStep}
                  onClick={() => handleIndicatorClick(stepNumber)}
                  animate={{ scale: status === "active" ? 1.08 : 1 }}
                  transition={transition}
                >
                  {status === "complete" ? (
                    <Check aria-hidden="true" />
                  ) : status === "active" ? (
                    <span className="onboarding-stepper__active-dot" aria-hidden="true" />
                  ) : (
                    <span>{stepNumber}</span>
                  )}
                </motion.button>

                {stepNumber < totalSteps && (
                  <span className="onboarding-stepper__connector" aria-hidden="true">
                    <motion.span
                      className="onboarding-stepper__connector-fill"
                      initial={false}
                      animate={{ scaleX: safeStep > stepNumber ? 1 : 0 }}
                      transition={transition}
                    />
                  </span>
                )}
              </div>
            );
          })}
        </div>

        <motion.div className="onboarding-stepper__content" layout transition={transition} aria-live="polite">
          <AnimatePresence initial={false} mode="wait" custom={direction}>
            <motion.div
              key={safeStep}
              className="onboarding-stepper__slide"
              custom={direction}
              variants={{
                enter: (slideDirection: number) => ({
                  x: prefersReducedMotion ? 0 : slideDirection >= 0 ? 36 : -36,
                  opacity: 0,
                }),
                center: { x: 0, opacity: 1 },
                exit: (slideDirection: number) => ({
                  x: prefersReducedMotion ? 0 : slideDirection >= 0 ? -24 : 24,
                  opacity: 0,
                }),
              }}
              initial="enter"
              animate="center"
              exit="exit"
              transition={transition}
              role="group"
              aria-label={`Onboarding step ${safeStep} of ${totalSteps}`}
            >
              {steps[safeStep - 1]}
            </motion.div>
          </AnimatePresence>
        </motion.div>

        <div className={`onboarding-stepper__footer ${safeStep === 1 ? "is-first-step" : ""}`}>
          {safeStep > 1 && (
            <button
              type="button"
              className="onboarding-stepper__back"
              onClick={onBack}
              disabled={loading}
            >
              {backButtonText}
            </button>
          )}

          <button
            type="button"
            className="onboarding-stepper__next"
            onClick={() => void onNext()}
            disabled={!canContinue || loading}
          >
            {loading ? (
              <>
                <span className="onboarding-stepper__spinner" aria-hidden="true" />
                <span className="sr-only">Working</span>
              </>
            ) : isLastStep ? (
              completeButtonText
            ) : (
              nextButtonText
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export function Step({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`onboarding-stepper__step ${className}`}>{children}</div>;
}
